import React, { ReactElement, useEffect } from "react";
import {
    BreadcrumbItem,
    IBlogPostPageProps,
    ICustomDomain,
} from "@genericTypes/sharedTypes";
import DefaultLayout from "@layout/default";
import SideWidget from "@components/shared/sideWidget";
import MainContent from "@components/blog";
import { useQuery } from "@tanstack/react-query";
import { GQLPosts, GQLDkis, GQLUrls, GQLCategories } from "@gql/types/gql";
import MetaTags from "@components/shared/metaTags";
import { translate } from "@helpers/translationObjectFormatter";
import { TranslationsObject } from "@genericTypes/sharedTypes";
import { extractExcerptFromBody, resolveURL } from "@lib/sharedUtils";
import {
    getMainTitlesNavigationFromBody,
    replaceDkis,
    titleDecoder,
    resolveNewsTagName,
} from "@lib/sharedUtils";
import Breadcrumb from "@components/shared/breadcrumb";
import initialCall, { initialCallPaths } from "@helpers/initial-call";
import { Session } from "src/api/session";

import { GetStaticPropsContext } from "next";
import { activePageAtom } from "@store/generalStore";
import { useSetAtom } from "jotai";
const Page = function CategoryPage({ title }: { title: string }) {
    const { data: props } = useQuery<IBlogPostPageProps, Error>({
        queryKey: ["blogPostPageProps"],
    });
    const session = useQuery<Session>({ queryKey: ["session"] });
    const { post: blog, category, relatedPosts, dkis } = props ?? {};

    const { data: translations } = useQuery<TranslationsObject>([
        "translations",
    ]);
    const setActivePage = useSetAtom(activePageAtom);
    useEffect(() => {
        setActivePage("news");
    }, []);

    const myList = [
        {
            title: translate(translations, "several_general_contents"),
            slug: "contents",
            content: getMainTitlesNavigationFromBody(blog?.body as string),
        },

        {
            title: translate(translations, "several_general_recentPosts"),
            slug: "recent posts",
            content: relatedPosts?.map((post) => ({
                ...post,
                title: titleDecoder(post.title, {
                    category: category
                        ? ((category.label ?? category.name) as string)
                        : "",
                    region:
                        (session?.data?.visitDetails?.region as string) ?? "",
                }),
            })) as { title: string | null; link: string }[],
        },
    ];
    const tagName = resolveNewsTagName(blog?.tags as { name: string }[]);

    const breadcrumbArr = [
        {
            label: translate(translations, "several_general_breadcrumb_home"),
            link: "/",
            isActive: true,
            showOnMobile: true,
        },
        {
            label: translate(translations, "several_general_newsWord"),
            link: "/news",
            isActive: true,
            showOnMobile: true,
        },
        {
            label: tagName,
            link: `/news/tag/${tagName?.toLowerCase() as string}`,
            isActive: true,
            showOnMobile: true,
        },

        {
            label: replaceDkis(
                resolveURL(blog?.url as GQLUrls),
                dkis as GQLDkis[],
            ),
            link: "#",
            isActive: false,
            showOnMobile: false,
        },
    ];
    const getDescription = (fallback: string) => {
        if (blog && blog?.metatags && blog?.metatags?.length) {
            for (let i = 0; i < blog?.metatags.length; i++) {
                if (
                    blog.metatags[i]?.key === "description" &&
                    blog.metatags[i]?.value
                ) {
                    return blog?.metatags[i]?.value;
                }
            }
        }
        if (blog?.excerpt) {
            return blog.excerpt;
        }
        if (blog?.body) {
            return extractExcerptFromBody(blog.body, 160);
        }

        return fallback;
    };
    const { data: domainInfo } = useQuery<ICustomDomain, Error>({
        queryKey: ["domain"],
    });

    const getKeyWords = () => {
        if (blog && blog?.metatags && blog?.metatags?.length) {
            for (let i = 0; i < blog?.metatags.length; i++) {
                if (
                    blog?.metatags[i]?.key === "keywords" &&
                    blog?.metatags[i]?.value
                ) {
                    return blog?.metatags[i]?.value;
                }
            }
        }

        if (blog?.tags) {
            return blog?.tags?.map((tag) => tag?.name).join(",");
        }
        for (let i = 0; i < (domainInfo?.domain?.metatags!.length ?? 0); i++) {
            if (
                domainInfo?.domain?.metatags![i]?.key === "keywords" &&
                domainInfo?.domain?.metatags[i]?.value
            ) {
                return domainInfo?.domain?.metatags[i]?.value;
            }
        }

        return "vpn , auto warranty";
    };

    return (
        <>
            <MetaTags
                title={`${
                    title ? title : (blog?.title as string)
                } | Several.com`}
                description={
                    getDescription(
                        translate(
                            translations,
                            "several_general_meta_description",
                        ),
                    ) as string
                }
                keyWords={getKeyWords() as string}
                image={blog?.image?.fullPath as string}
                url={blog?.url?.url as string}
                type="article"
            />
            <section className="max-w-7xl mx-auto px-5">
                <Breadcrumb arr={breadcrumbArr as BreadcrumbItem[]} />
                <div className=" flex flex-col-reverse lg:flex-row lg:items-start items-center lg:my-16 mb-9">
                    <SideWidget
                        myList={myList}
                        contentToBeSharedUrl={blog?.url?.url as string}
                        category={category as GQLCategories}
                    />
                    <MainContent
                        blog={blog as GQLPosts}
                        category={category}
                        listOfContentsMobile={myList[0].content}
                        recentPostsMobile={myList[1].content}
                        isNews
                    />
                </div>
            </section>
        </>
    );
};

Page.getLayout = function getLayout(page: ReactElement) {
    return <DefaultLayout pageType="newsId">{page}</DefaultLayout>;
};

export function getStaticPaths() {
    return initialCallPaths();
}
export async function getStaticProps(ctx: GetStaticPropsContext) {
    return await initialCall("NewsId", ctx);
}

export default Page;
